import type { MediaStream as ZoomMediaStream } from '../index-types';
import { useRef, useState, useCallback, useEffect } from 'react';
import useLogger from './useLogger';
import { VideoQuality } from '@zoom/videosdk';
import { detectDeviceType, isMobile } from '../utils/DeviceType';

type UseSegmentedZoomRecorderOptions = {
  stream: ZoomMediaStream | null;
  userId: number;
  segmentDurationSec?: number;
  isStartedVideo: boolean;
  isMeeting: boolean;
  onSegmentAvailable: (blob: Blob, startedAt: number) => void;
  leave: boolean;
};

export const useSegmentedZoomRecorder = ({
  stream,
  userId,
  segmentDurationSec = 180,
  isStartedVideo,
  isMeeting,
  leave,

  onSegmentAvailable
}: UseSegmentedZoomRecorderOptions) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const segmentTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const startedAtRef = useRef<number>(0); // 録画開始タイムスタンプ
  const logger = useLogger();
  const detectedDevice = detectDeviceType();
  const ctx = canvasRef.current?.getContext('2d');
  const [videoRecTermination, setVideoRecTermination] = useState(false);

  const drawToCanvas = useCallback(() => {
    if (!ctx || !videoRef.current || !canvasRef.current) {
      return;
    }
    const videoH = videoRef.current.videoHeight;
    const videoW = videoRef.current.videoWidth;
    const videoAspect = videoW / videoH;
    const canvasH = canvasRef.current.height;
    const canvasW = canvasRef.current.width;
    const canvasAspect = canvasW / canvasH;
    let drawWidth, drawHeight, offsetX, offsetY;
    if (videoAspect > canvasAspect) {
      // 横長の場合
      drawWidth = Math.min(videoW, canvasW);
      drawHeight = drawWidth / videoAspect;
    } else {
      // 縦長の場合
      drawHeight = Math.min(videoH, canvasH);
      drawWidth = drawHeight * videoAspect;
    }
    offsetX = (canvasW - drawWidth) / 2;
    offsetY = (canvasH - drawHeight) / 2;
    ctx.clearRect(0, 0, canvasW, canvasH);

    ctx.drawImage(videoRef.current, 0, 0, videoW, videoH, offsetX, offsetY, drawWidth, drawHeight);
    requestAnimationFrame(drawToCanvas);
  }, [videoRef, canvasRef, ctx]);

  const createCombinedStream = (canvas: HTMLCanvasElement, audioTrack: MediaStreamTrack): MediaStream => {
    const canvasStream = canvas.captureStream(30);
    const audioContext = new AudioContext();
    const audioStream = new MediaStream([audioTrack]);
    const sourceNode = audioContext.createMediaStreamSource(audioStream);
    const destinationNode = audioContext.createMediaStreamDestination();
    sourceNode.connect(destinationNode);
    const combinedStream = new MediaStream([
      ...canvasStream.getVideoTracks(),
      ...destinationNode.stream.getAudioTracks()
    ]);
    return combinedStream;
  };

  const startSegment = useCallback(async () => {
    if (isRecording) {
      return;
    }
    if (!userId) {
      return;
    }
    // if (!isStartedVideo && !isStartedAudio) {
    //   return;
    // }
    if (!canvasRef.current || !stream) {
      logger.warn('録画開始に失敗しました: streamがありません');
      return;
    }

    startedAtRef.current = Date.now(); // 開始時刻セット

    let media: MediaStreamConstraints = { video: false };
    const camera = stream.getActiveCamera();
    const microphone = stream.getActiveMicrophone();
    logger.log(camera, microphone);
    // if (isStartedVideo) {
    //   media = { ...media, video: camera ? { deviceId: { exact: camera } } : true };
    // }
    try {
      // if (isMobile()) {
      if (false) {
        // stream.renderVideo(canvasRef.current, userId, 640, 480, 0, 0, VideoQuality.Video_720P);
      } else {
        const userVideo = await stream.attachVideo(userId, VideoQuality.Video_720P);
        const vpc = document.querySelector('#rec>video-player-container');
        if (!vpc) {
          return;
        }
        if (vpc.firstChild) {
          vpc.removeChild(vpc.firstChild);
        }
        const vp = vpc.appendChild(userVideo as any);
        const userVideoElement = (vp as HTMLElement)?.querySelector('video');
        if (!userVideoElement) {
          return;
        }
        videoRef.current = userVideoElement;
        drawToCanvas();
      }

      let mediaStream: MediaStream;
      if (false) {
        // media = { ...media, audio: microphone ? { deviceId: { exact: microphone } } : true };
        // const localAudioStream = await navigator.mediaDevices.getUserMedia(media);
        // const audioTrack = localAudioStream.getAudioTracks()[0];
        // const combinedStream = createCombinedStream(canvasRef.current, audioTrack);
        // mediaStream = combinedStream;
      } else {
        const captureStream = canvasRef.current.captureStream(30);
        mediaStream = captureStream;
      }

      const options = (() => {
        logger.log(detectedDevice);
        if (detectedDevice.os === 'ios') {
          return { mimeType: 'video/mp4' };
        } else if (MediaRecorder.isTypeSupported('video/webm; codecs=vp9')) {
          return { mimeType: 'video/webm; codecs=vp9' };
        } else if (MediaRecorder.isTypeSupported('video/webm')) {
          return { mimeType: 'video/webm' };
        } else if (MediaRecorder.isTypeSupported('video/mp4')) {
          return { mimeType: 'video/mp4', videoBitsPerSecond: 100000 };
        } else {
          return {};
        }
      })();

      logger.log(options);
      const recorder = new MediaRecorder(mediaStream, options);
      logger.log('created mediarocorder', recorder);

      const chunks: Blob[] = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'video/webm' });
        onSegmentAvailable(blob, startedAtRef.current); // タイムスタンプ付きコールバック
        chunks.length = 0;
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      logger.log('recorder start');

      setIsRecording(true);

      segmentTimerRef.current = setTimeout(() => {
        recorder.stop();
        setTimeout(startSegment, 0);
      }, segmentDurationSec * 1000);
    } catch (error) {
      logger.error(error);
    }
  }, [stream, userId, segmentDurationSec, isStartedVideo, onSegmentAvailable]);

  const stopRecording = useCallback(
    (leave: boolean) => {
      if (segmentTimerRef.current) {
        clearTimeout(segmentTimerRef.current);
      }
      mediaRecorderRef.current?.stop();
      setIsRecording(false);
      if (!leave) {
        setTimeout(startSegment, 0);
        return;
      }
      setVideoRecTermination(true);
    },
    [isMeeting, isStartedVideo]
  );

  useEffect(() => {
    return () => {
      if (leave) {
        stopRecording(true);
      }
    };
  }, [stopRecording, leave]);

  return {
    canvasRef,
    videoRef,
    startRecording: startSegment,
    stopRecording,
    isRecording,
    videoRecTermination
  };
};
